import type { LucideIcon } from "lucide-react";
import type { FC, FormEvent, ReactNode } from "react";
import {
  FormProvider,
  type FieldValues,
  type SubmitHandler,
  type UseFormReturn
} from "react-hook-form";

import { cn } from "@/lib/utils";

type FormProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  /** Runs with validated values; omit for forms submitted from elsewhere. */
  onSubmit?: SubmitHandler<T>;
  id?: string;
  className?: string;
  children: ReactNode;
};

/**
 * `FormProvider` + the `<form>` element. Native validation is off so zod
 * owns every message; Enter in a text input submits.
 */
export function Form<T extends FieldValues>({
  form,
  onSubmit,
  id,
  className,
  children
}: FormProps<T>) {
  const handleSubmit = onSubmit
    ? form.handleSubmit(onSubmit)
    : (e: FormEvent) => e.preventDefault();
  return (
    <FormProvider {...form}>
      <form
        id={id}
        noValidate
        className={cn("flex w-full flex-col gap-5", className)}
        onSubmit={(e) => {
          Promise.resolve(handleSubmit(e)).catch(() => undefined);
        }}
      >
        {children}
      </form>
    </FormProvider>
  );
}

type FormRowProps = {
  children: ReactNode;
  className?: string;
  /**
   * Container width at which the row goes side by side (Tailwind container
   * breakpoints: sm 24rem, md 28rem, lg 32rem). Use `lg` for 4+ fields.
   */
  collapseBelow?: "sm" | "md" | "lg";
};

const ROW_BREAKPOINT: Record<
  NonNullable<FormRowProps["collapseBelow"]>,
  string
> = {
  sm: "@sm/form-row:flex-row @sm/form-row:items-start",
  md: "@md/form-row:flex-row @md/form-row:items-start",
  lg: "@lg/form-row:flex-row @lg/form-row:items-start"
};

/**
 * Fields side by side when the row itself is wide enough, stacked otherwise.
 * Measures its own width, so it behaves the same in a narrow sheet and a
 * full page.
 */
export const FormRow: FC<FormRowProps> = ({
  children,
  className,
  collapseBelow = "md"
}) => (
  <div className={cn("w-full @container/form-row", className)}>
    <div
      className={cn(
        "flex w-full flex-col gap-4 [&>*]:min-w-0 [&>*]:flex-1 [&>*]:basis-0",
        ROW_BREAKPOINT[collapseBelow]
      )}
    >
      {children}
    </div>
  </div>
);

type FormSectionProps = {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  icon?: LucideIcon;
  /** `card`: ringed card. `band`: full-bleed header band. `plain`: heading only. */
  variant?: "card" | "band" | "plain";
  /** Deep-link anchor. */
  id?: string;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
};

/** Titled group of fields; the one heading style every form uses. */
export const FormSection: FC<FormSectionProps> = ({
  title,
  description,
  action,
  icon: Icon,
  variant = "card",
  id,
  className,
  bodyClassName,
  children
}) => {
  const heading = (
    <div className="flex min-w-0 items-center gap-2">
      {Icon ? (
        <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      ) : null}
      <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        {title}
      </span>
      {description ? (
        <span className="truncate text-xs text-fg-3">{description}</span>
      ) : null}
    </div>
  );
  const actions = action ? (
    <div className="flex shrink-0 items-center gap-1">{action}</div>
  ) : null;

  if (variant === "plain") {
    return (
      <section
        id={id}
        className={cn(
          "flex w-full min-w-0 flex-col gap-3 scroll-mt-4",
          className
        )}
      >
        <div className="flex items-center justify-between gap-2">
          {heading}
          {actions}
        </div>
        <div className={cn("flex w-full flex-col gap-4", bodyClassName)}>
          {children}
        </div>
      </section>
    );
  }

  if (variant === "band") {
    return (
      <section
        id={id}
        className={cn("flex w-full min-w-0 flex-col scroll-mt-4", className)}
      >
        <div className="flex min-h-9 w-full items-center justify-between gap-2 border-y border-border bg-fill-1 px-6 py-2">
          {heading}
          {actions}
        </div>
        <div
          className={cn("flex w-full flex-col gap-5 px-6 py-5", bodyClassName)}
        >
          {children}
        </div>
      </section>
    );
  }

  return (
    <section
      id={id}
      className={cn(
        "flex w-full min-w-0 flex-col overflow-hidden rounded-lg bg-card text-card-foreground ring-1 ring-foreground/10 scroll-mt-4",
        className
      )}
    >
      <header className="flex min-h-9 shrink-0 items-center justify-between gap-2 border-b border-border bg-fill-1 px-3 py-1.5">
        {heading}
        {actions}
      </header>
      <div
        className={cn("flex w-full flex-col gap-4 px-4 py-4", bodyClassName)}
      >
        {children}
      </div>
    </section>
  );
};

/** Small bold caption that splits a section into sub-groups. */
export const FormSubheading: FC<{
  children: ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <span className={cn("text-xs font-semibold text-foreground", className)}>
    {children}
  </span>
);
